/**
 * The league lobby (docs/game-design.md §7.4, L3), in the matchmaking screen's frame: today's
 * opponent is known, so their card shows from the start. Waiting for a player: "Waiting for X…",
 * the window's countdown, and "Fight X's game plan" to start now. A bot's place, or both there:
 * the fight starts straight away. Then VS, as for a quick fight.
 */
import { levelOf, lookOf, type Look } from '@caged/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { FighterArt } from '../components/FighterArt';
import ui from '../components/ui.module.css';
import { clock } from '../format';
import { useKey, useLayoutMode, useMyFighter, useShortLandscape } from '../hooks';
import '../ui.css';
import s from './MatchmakingScreen.module.css';

const FOUND_HOLD = 1500;

/** Why the lobby couldn't be joined, as the player reads it. */
function problemKey(reason: string): string {
  if (reason.includes('taken')) return 'screens.lobby.taken';
  if (reason.includes('window')) return 'screens.lobby.closed';
  if (reason.includes('update')) return 'screens.lobby.update';
  return 'screens.lobby.failed';
}

export function LeagueLobby() {
  const { t } = useTranslation();
  const mode = useLayoutMode();
  const short = useShortLandscape();
  const mm = useApp((st) => st.matchmaking);
  const match = useApp((st) => st.match);
  const settings = useApp((st) => st.settings);
  const myFighter = useMyFighter();
  const progress = useApp((st) => st.progress);
  const go = useApp((st) => st.go);
  const openLeague = useApp((st) => st.openLeague);
  const cancelMatchmaking = useApp((st) => st.cancelMatchmaking);
  const fightPlanNow = useApp((st) => st.fightPlanNow);
  const [now, setNow] = useState(() => Date.now());
  const [asked, setAsked] = useState(false);
  const lobby = mm?.league?.lobby ?? null;
  const failed = mm?.league?.failed;
  const found = !!match && mm?.status === 'found';

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!found) return;
    const id = setTimeout(() => go('vs'), FOUND_HOLD);
    return () => clearTimeout(id);
  }, [found, go]);

  const cancel = useCallback(() => {
    cancelMatchmaking();
    openLeague('league');
  }, [cancelMatchmaking, openLeague]);
  useKey('Escape', cancel);

  const me = match?.player ?? { fighterId: myFighter, name: settings.playerName || 'You', level: levelOf(progress).level, look: lookOf(progress) };
  const opp = match?.opponent ?? lobby?.opponent ?? null;
  const oppName = opp?.name ?? '';
  const plan = !!lobby && lobby.opponent.human && !lobby.opponent.here;
  const left = lobby ? Math.max(0, (lobby.windowEndsAt - now) / 1000) : 0;

  let title: string;
  let sub: string;
  if (failed) {
    title = t('screens.lobby.cantJoin');
    sub = t(problemKey(failed));
  } else if (found) {
    title = match?.online?.info.league?.plan ? t('screens.lobby.vsPlan', { name: oppName }) : match?.online?.info.vsBot ? t('screens.lobby.vsBot') : t('screens.lobby.bothHere');
    sub = t('screens.matchmaking.getReady');
  } else if (!lobby) {
    title = t('screens.lobby.entering');
    sub = '';
  } else if (plan) {
    title = t('screens.lobby.waitingFor', { name: oppName });
    sub = t('screens.lobby.windowCloses', { time: clock(left) });
  } else {
    title = t('screens.lobby.starting');
    sub = t('screens.matchmaking.getReady');
  }

  const artBox = (imgH: number) => ({ height: Math.round(imgH * 1.2) });
  const card = (f: { fighterId: string; name: string; look?: Look } | null, tag: string, imgH: number, flip: boolean, lit: boolean) => (
    <div className={[s.card, lit ? s.cardFound : ''].join(' ')}>
      <div className={s.cardArt} style={artBox(imgH)}>
        {f ? (
          <FighterArt id={f.fighterId} height={imgH} flip={flip} shadow="soft" alt={f.name} look={f.look} />
        ) : (
          <div className={s.unknown} style={{ width: imgH, height: imgH }} aria-hidden="true">
            ?
          </div>
        )}
      </div>
      <div className={s.cardName}>{f?.name ?? '???'}</div>
      <div className={s.cardTag}>{tag}</div>
    </div>
  );
  const meTag = t('screens.matchmaking.youTag', { level: me.level ?? 1 });
  const oppTag = !lobby ? '' : !lobby.opponent.human ? t('screens.league.bot') : lobby.opponent.here || (found && !match?.online?.info.league?.plan) ? t('screens.lobby.here') : t('screens.lobby.away');

  const head = (
    <>
      <h1 className={s.title} aria-live="polite">
        {title}
      </h1>
      <div className={[s.sub, found ? s.subFound : ''].join(' ')}>{sub}</div>
    </>
  );
  const actions = failed ? (
    <button type="button" className={ui.primary} onClick={cancel}>
      {t('screens.lobby.backToLeague')}
      <span className={ui.arrow} aria-hidden="true">
        →
      </span>
    </button>
  ) : (
    <>
      {plan && !found && (
        <button
          type="button"
          className={ui.primary}
          disabled={asked}
          onClick={() => {
            setAsked(true);
            fightPlanNow();
          }}
        >
          {t('screens.lobby.fightPlan', { name: oppName })}
          <span className={ui.arrow} aria-hidden="true">
            ▷
          </span>
        </button>
      )}
      {!found && (
        <button type="button" className={ui.ghost} onClick={cancel}>
          {t('common.cancel')}
        </button>
      )}
    </>
  );
  const top = (
    <div className={s.topRow}>
      <span>{t('screens.lobby.leagueFight', { day: lobby?.day ?? match?.online?.info.league?.day ?? '' })}</span>
      <span>{plan && !found ? t('screens.lobby.waiting') : ''}</span>
    </div>
  );

  if (mode === 'landscape') {
    return (
      <div className={['cg-ui', s.root].join(' ')}>
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.9),rgba(20,19,18,.4) 45%,rgba(20,19,18,.95))" />
        <div className={s.land}>
          {top}
          <div className={s.landMid}>
            {card(me, meTag, short ? 60 : 96, false, true)}
            <div className={s.landCenter}>{head}</div>
            {card(opp, oppTag, short ? 60 : 96, true, found || !!lobby?.opponent.here)}
          </div>
          <div className={s.landBottom}>{actions}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={['cg-ui', s.root].join(' ')}>
      <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.9),rgba(20,19,18,.4) 45%,rgba(20,19,18,.95))" />
      <div className={s.port}>
        {top}
        <div className={s.portMid}>
          <div className={s.portTitle}>{head}</div>
          <div className={s.portCards}>
            {card(me, meTag, 84, false, true)}
            <div className={s.vs} aria-hidden="true">
              VS
            </div>
            {card(opp, oppTag, 84, true, found || !!lobby?.opponent.here)}
          </div>
        </div>
        <div className={s.portBottom}>{actions}</div>
      </div>
    </div>
  );
}
